// LLMCostTile.js — Phase 1 anchor tile.
// Shows 7-day + 30-day LLM cost totals and a 30-day line chart.
// Data source: llm_usage_daily (uid="system" docs), via useLLMDailySystemSeries.

import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useLLMDailySystemSeries, useLLMTodayCost, toCSV, downloadCSV } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${n.toFixed(2)}`;

const LLMCostTile = () => {
  const { series, totals, loading, error } = useLLMDailySystemSeries(30);
  const { today, loading: todayLoading } = useLLMTodayCost();

  const handleCSV = () => {
    downloadCSV(
      'llm-cost-30d.csv',
      toCSV(series, [
        { key: 'date', header: 'date' },
        { key: 'cost_usd', header: 'cost_usd' },
        { key: 'calls', header: 'calls' },
      ]),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">LLM cost unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>LLM Cost</Typography>
          <MuiTooltip title="Download CSV">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading || series.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Today (so far)</Typography>
            <Typography variant="h4">{todayLoading ? '…' : formatUSD(today.cost_usd)}</Typography>
            <Typography variant="caption" color="text.secondary">· {todayLoading ? '…' : today.calls} calls</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last 7 days</Typography>
            <Typography variant="h4">{loading ? '…' : formatUSD(totals.last7)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last 30 days</Typography>
            <Typography variant="h4">{loading ? '…' : formatUSD(totals.last30)}</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: 240 }}>
          {series.length === 0 && !loading ? (
            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
          ) : (
            <ResponsiveContainer>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={formatUSD} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Line type="monotone" dataKey="cost_usd" stroke="#1976d2" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Total spend on Anthropic Claude API calls across every Maia function (decision engine, web chat, etc.).
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LLMCostTile;
